import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';

// "Spazio di lavoro" dell'utente: adatta sidebar e Home al mestiere di chi usa l'app.
//   completo    → tutte le sezioni (comportamento storico)
//   videomaker  → produzione visual: cosa girare/montare, file da consegnare
//   social      → Social Media Manager: copy, approvazioni, programmazione
// Salvato sull'account (/api/me/preferenze) così segue la persona su ogni dispositivo;
// il browser ne tiene una copia per partire subito (e come ripiego se il server
// non ha ancora la tabella delle preferenze).
export const WORKSPACES = ['completo', 'videomaker', 'social'];
const EVENT = 'eden-workspace-change';
const sincronizzati = new Set();   // utenti già allineati col server in questa sessione

function storageKey(user) {
  return `eden-workspace:${user?.id || 'anon'}`;
}

function read(user) {
  try {
    const v = localStorage.getItem(storageKey(user));
    return WORKSPACES.includes(v) ? v : null;
  } catch {
    return null;
  }
}

function writeLocal(user, ws) {
  try { localStorage.setItem(storageKey(user), ws); } catch {}
  window.dispatchEvent(new CustomEvent(EVENT));
}

export function useWorkspace() {
  const { user } = useAuth();
  // null = mai scelto (la Home propone la scelta), altrimenti uno dei WORKSPACES
  const [scelto, setScelto] = useState(() => read(user));

  useEffect(() => { setScelto(read(user)); }, [user]);

  // Una volta per sessione: la scelta salvata sull'account vince su quella del browser;
  // se l'account non ne ha ancora una ma il browser sì, la carica sul server.
  useEffect(() => {
    if (!user?.id || sincronizzati.has(user.id)) return;
    sincronizzati.add(user.id);
    api.get('/me/preferenze', { silent: true }).then(r => {
      if (!r.data?.persistito) return;
      const remoto = r.data.dati?.workspace;
      const locale = read(user);
      if (WORKSPACES.includes(remoto) && remoto !== locale) writeLocal(user, remoto);
      else if (!remoto && locale) api.put('/me/preferenze', { workspace: locale }, { silent: true }).catch(() => {});
    }).catch(() => {});
  }, [user]);

  // Sincronizza tutte le istanze (sidebar, Home, bacheca) quando cambia lo spazio.
  useEffect(() => {
    const h = () => setScelto(read(user));
    window.addEventListener(EVENT, h);
    return () => window.removeEventListener(EVENT, h);
  }, [user]);

  const setWorkspace = useCallback((ws) => {
    setScelto(ws);
    writeLocal(user, ws);
    api.put('/me/preferenze', { workspace: ws }, { silent: true }).catch(() => {});
  }, [user]);

  // Senza una scelta esplicita, i ruoli operativi partono dal loro spazio.
  const perRuolo = { videomaker: 'videomaker', social_manager: 'social' }[user?.ruolo];
  const workspace = scelto || perRuolo || 'completo';
  return { workspace, scelto: scelto !== null || !!perRuolo, focus: workspace !== 'completo', setWorkspace };
}
